import React from "react";
import { Box, Typography, Divider, Chip } from "@mui/material";
import { PreventivoRow } from "../../services/preventiviApi";
import { getRowMetadata, hasValidMatch } from "../../utils/matchSelection";
import { isNpRow } from "../../utils/npCodes";

interface AggregationFooterProps {
  rows: PreventivoRow[];
}

const _fmt2 = (v: number) =>
  v.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const getVal = (row: PreventivoRow, key: string): number | null => {
  const v = getRowMetadata(row)[key];
  if (v == null) return null;
  const n = parseFloat(String(v));
  return isNaN(n) ? null : n;
};

export const AggregationFooter: React.FC<AggregationFooterProps> = ({ rows }) => {
  const itemRows = rows.filter((r) => (r.row_type ?? "item") === "item");
  const npRows = itemRows.filter((r) => isNpRow(r));
  const npMissing = npRows.filter((r) => !hasValidMatch(r)).length;
  const matchableRows = itemRows.filter((r) => !isNpRow(r));
  const confirmed = matchableRows.filter(hasValidMatch).length;
  const pending = matchableRows.length - confirmed;

  let totalPrezzo = 0;
  let totalMat = 0;
  let totalPosa = 0;
  let totalMano = 0;

  for (const r of itemRows) {
    const qty = Number(r.quantity) || 1;
    const prezzo = getVal(r, "prezzo");
    const mat = getVal(r, "costo_mat_unitario");
    const posa = getVal(r, "tempo_posa_ore");
    const mano = getVal(r, "costo_mano_unitario");
    if (prezzo != null) totalPrezzo += prezzo * qty;
    if (mat != null) totalMat += mat * qty;
    if (posa != null) totalPosa += posa * qty;
    if (mano != null) totalMano += mano * qty;
  }

  const Metric: React.FC<{ label: string; value: string; color: string; unit?: string }> = ({
    label, value, color, unit = "€",
  }) => (
    <Box>
      <Typography variant="caption" color="text.disabled" sx={{ fontSize: "0.68rem", display: "block", lineHeight: 1.2 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 700, color, fontSize: "0.82rem", lineHeight: 1.3 }}>
        {value} <Typography component="span" variant="caption" sx={{ color, fontWeight: 400 }}>{unit}</Typography>
      </Typography>
    </Box>
  );

  return (
    <Box
      sx={{
        position: "sticky",
        bottom: 0,
        bgcolor: "background.paper",
        borderTop: "2px solid",
        borderColor: "divider",
        px: 2,
        py: 0.75,
        display: "flex",
        alignItems: "center",
        gap: 2,
        flexWrap: "wrap",
        zIndex: 10,
      }}
    >
      {/* Totale materiali */}
      <Metric label="Tot. Materiali" value={_fmt2(totalMat)} color="#1565C0" />
      <Divider orientation="vertical" flexItem />

      {/* Totale posa */}
      <Metric label="Tot. Posa" value={_fmt2(totalPosa)} color="#2E7D32" unit="h" />
      <Divider orientation="vertical" flexItem />

      {/* Totale manodopera */}
      <Metric label="Tot. Manodopera" value={_fmt2(totalMano)} color="#E65100" />
      <Divider orientation="vertical" flexItem />

      {/* Totale prezzi di vendita */}
      <Box>
        <Typography variant="caption" color="text.disabled" sx={{ fontSize: "0.68rem", display: "block", lineHeight: 1.2 }}>
          Tot. P.V.
        </Typography>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "text.primary", fontSize: "0.86rem", lineHeight: 1.3 }}>
          {_fmt2(totalPrezzo)} €
        </Typography>
      </Box>

      <Box sx={{ flex: 1 }} />

      {/* Row counters */}
      <Box sx={{ display: "flex", gap: 0.75, "& .MuiChip-root": { height: 22, fontSize: "0.72rem" } }}>
        <Chip label={`${itemRows.length} voci`} size="small" sx={{ bgcolor: "#F5F5F5", fontWeight: 500 }} />
        <Chip label={`${confirmed} con match`} size="small" sx={{ bgcolor: "#E8F5E9", color: "#2E7D32", fontWeight: 500 }} />
        {pending > 0 && (
          <Chip label={`${pending} in attesa`} size="small" sx={{ bgcolor: "#FFF3E0", color: "#E65100", fontWeight: 500 }} />
        )}
        {npRows.length > 0 && (
          <Chip
            label={`${npMissing}/${npRows.length} NP mancanti`}
            size="small"
            sx={{ bgcolor: "#FFFDE7", color: "#9E8B00", fontWeight: 500 }}
          />
        )}
      </Box>
    </Box>
  );
};
